/**
 * Vector floor plan stored as a project asset (`plan-….json`, VECTOR_PLAN_MIME): polylines and
 * texts in real-world millimetres, y up (as in CAD), grouped by layer. A sheet background
 * places its bounds into a rectangle of the sheet, like an image; at the sheet's drawing scale
 * that rectangle is the plan's true size.
 */

export const VECTOR_PLAN_MIME = 'application/vnd.tablo.plan+json';

export interface PlanText {
  x: number;
  y: number;
  /** Height (mm, real world). */
  h: number;
  /** Degrees, counter-clockwise (CAD). */
  rot: number;
  text: string;
  /** Horizontal alignment at (x, y). */
  align: 'start' | 'middle' | 'end';
}

export interface PlanLayer {
  name: string;
  /** Polylines as flat [x0, y0, x1, y1, …] (mm). */
  lines: number[][];
  texts: PlanText[];
}

export interface VectorPlan {
  v: 1;
  /** [minX, minY, maxX, maxY] in mm. */
  bounds: [number, number, number, number];
  layers: PlanLayer[];
}

export const planSize = (p: VectorPlan) => ({
  width: p.bounds[2] - p.bounds[0],
  height: p.bounds[3] - p.bounds[1],
});

/** Plan → sheet coordinates for a background placed at rect (sheet mm, y down). */
export function planToSheet(p: VectorPlan, rect: { x: number; y: number; width: number; height: number }) {
  const { width, height } = planSize(p);
  const kx = rect.width / (width || 1);
  const ky = rect.height / (height || 1);
  const [minX, , , maxY] = p.bounds;
  return {
    kx,
    ky,
    x: (px: number) => rect.x + (px - minX) * kx,
    y: (py: number) => rect.y + (maxY - py) * ky,
  };
}

const r1 = (n: number) => Math.round(n * 10) / 10;

/** Compact JSON (coordinates to 0.1 mm). */
export function serializePlan(p: VectorPlan): Uint8Array {
  const round: VectorPlan = {
    v: 1,
    bounds: p.bounds.map(r1) as VectorPlan['bounds'],
    layers: p.layers.map((l) => ({
      name: l.name,
      lines: l.lines.map((pl) => pl.map(r1)),
      texts: l.texts.map((t) => ({ ...t, x: r1(t.x), y: r1(t.y), h: r1(t.h), rot: r1(t.rot) })),
    })),
  };
  return new TextEncoder().encode(JSON.stringify(round));
}

export function parsePlan(bytes: Uint8Array): VectorPlan | null {
  try {
    const v = JSON.parse(new TextDecoder().decode(bytes)) as VectorPlan;
    if (v?.v !== 1 || !Array.isArray(v.bounds) || !Array.isArray(v.layers)) return null;
    return v;
  } catch {
    return null;
  }
}

/** Segment count (for the import summary and limits). */
export const planSegments = (p: VectorPlan) =>
  p.layers.reduce((n, l) => n + l.lines.reduce((m, pl) => m + Math.max(0, pl.length / 2 - 1), 0), 0);

/**
 * SVG path data of one layer's polylines in sheet coordinates (or scaled further by `k`, e.g.
 * to PDF points). Coordinates rounded to 0.01.
 */
export function layerPath(
  p: VectorPlan,
  layer: PlanLayer,
  rect: { x: number; y: number; width: number; height: number },
  k = 1,
): string {
  const m = planToSheet(p, rect);
  const f = (n: number) => String(Math.round(n * k * 100) / 100);
  const parts: string[] = [];
  for (const pl of layer.lines) {
    if (pl.length < 4) continue;
    let s = `M${f(m.x(pl[0]!))} ${f(m.y(pl[1]!))}`;
    for (let i = 2; i < pl.length; i += 2) s += `L${f(m.x(pl[i]!))} ${f(m.y(pl[i + 1]!))}`;
    parts.push(s);
  }
  return parts.join('');
}
